import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FileText, Sparkles, Download, History, ArrowRight, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';

const Landing = () => {
  const navigate = useNavigate();

  const features = [
    {
      icon: <Sparkles className="w-6 h-6" />,
      title: 'AI-Powered Tailoring',
      description: 'Advanced GPT-5.1 analyzes your resume and job description to create perfect matches'
    },
    {
      icon: <FileText className="w-6 h-6" />,
      title: 'ATS-Friendly Format',
      description: 'Single-column, clean formatting that passes all Applicant Tracking Systems'
    },
    {
      icon: <Download className="w-6 h-6" />,
      title: 'Multiple Formats',
      description: 'Download your tailored resume and cover letter in both PDF and DOCX'
    },
    {
      icon: <History className="w-6 h-6" />,
      title: 'Save Sessions',
      description: 'Store and revisit previous tailoring sessions for different applications'
    }
  ];

  const steps = [
    'Paste the job description',
    'Upload your resume (PDF/DOCX)',
    'AI analyzes and tailors your resume',
    'Generate matching cover letter',
    'Download and apply'
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 dark:from-slate-950 dark:via-blue-950 dark:to-slate-900">
      {/* Header */}
      <header className="border-b border-slate-200 dark:border-slate-800 bg-white/70 dark:bg-slate-900/70 backdrop-blur-xl">
        <div className="container mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
              <FileText className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-2xl font-bold tracking-tight">Dashworx</h1>
          </div>
          <Button
            data-testid="view-saved-sessions-btn"
            variant="ghost"
            onClick={() => navigate('/sessions')}
          >
            <History className="w-4 h-4 mr-2" />
            Saved Sessions
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="container mx-auto px-4 sm:px-6 py-12 sm:py-20">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight mb-4 sm:mb-6 leading-tight">
              Tailor Your Resume
              <br />
              <span className="text-primary">In Minutes</span>
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-muted-foreground mb-6 sm:mb-8 leading-relaxed max-w-2xl mx-auto px-2">
              AI-powered resume tailoring that adapts your experience to match any job description.
              ATS-friendly, recruiter-ready resumes with matching cover letters.
            </p>
            <p className="text-sm sm:text-base text-muted-foreground mb-6 sm:mb-8 max-w-xl mx-auto px-2">
              <strong>✨ Smart Feature:</strong> Upload your resume once, then simply paste new job descriptions to get instantly tailored resumes. No need to re-upload!
            </p>
            <Button
              data-testid="get-started-btn"
              size="lg"
              className="h-12 sm:h-14 px-6 sm:px-8 text-base sm:text-lg font-semibold shadow-lg hover:shadow-xl transition-all active:scale-95 w-full sm:w-auto"
              onClick={() => navigate('/wizard')}
            >
              Get Started
              <ArrowRight className="ml-2 w-4 h-4 sm:w-5 sm:h-5" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="container mx-auto px-4 sm:px-6 py-8 sm:py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 max-w-6xl mx-auto">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <Card className="p-6 hover:shadow-lg transition-shadow h-full">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4 text-primary">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                <p className="text-sm text-muted-foreground">{feature.description}</p>
              </Card>
            </motion.div>
          ))}
        </div>
      </section>

      {/* How It Works */}
      <section className="container mx-auto px-4 sm:px-6 py-8 sm:py-16">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center mb-8 sm:mb-12">How It Works</h2>
          <div className="space-y-4">
            {steps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="flex items-center gap-4 bg-white dark:bg-slate-900 rounded-xl p-6 shadow-sm"
              >
                <div className="flex-shrink-0 w-10 h-10 bg-primary text-white rounded-full flex items-center justify-center font-bold">
                  {index + 1}
                </div>
                <p className="text-lg">{step}</p>
                <CheckCircle2 className="ml-auto w-6 h-6 text-emerald-500" />
              </motion.div>
            ))}
          </div>
          <div className="text-center mt-12">
            <Button
              data-testid="start-tailoring-btn"
              size="lg"
              className="h-14 px-8 text-lg font-semibold shadow-lg hover:shadow-xl transition-all active:scale-95"
              onClick={() => navigate('/wizard')}
            >
              Start Tailoring Now
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-200 dark:border-slate-800 mt-20">
        <div className="container mx-auto px-6 py-8 text-center text-sm text-muted-foreground">
          <p>© 2025 Dashworx Resume Tailor. Powered by AI.</p>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
